import React from 'react';
import type { CardComponent, CardAction } from '../../schemas/card';
import { cn } from '../../utils/cn';

interface CardRendererProps {
    component: CardComponent;
    onAction?: (action: string) => void;
    className?: string;
}

/**
 * Render a card component using ShadCN-style card
 */
export function CardRenderer({ component, onAction, className }: CardRendererProps) {
    const { title, subtitle, content, image_url, actions, variant = 'default' } = component;

    const variantStyles = {
        default: 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700',
        outlined: 'bg-transparent border-2 border-gray-300 dark:border-gray-600',
        elevated: 'bg-white dark:bg-gray-900 shadow-lg',
    };

    return (
        <div
            className={cn(
                'rounded-lg overflow-hidden',
                variantStyles[variant],
                className
            )}
        >
            {image_url && (
                <div className="aspect-video w-full overflow-hidden">
                    <img
                        src={image_url}
                        alt={title}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
            <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h3>
                {subtitle && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {subtitle}
                    </p>
                )}
                {content && (
                    <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                        {content}
                    </p>
                )}
                {actions && actions.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {actions.map((action, index) => (
                            <CardActionButton
                                key={index}
                                action={action}
                                onClick={() => onAction?.(action.action)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

interface CardActionButtonProps {
    action: CardAction;
    onClick?: () => void;
}

function CardActionButton({ action, onClick }: CardActionButtonProps) {
    const { label, variant = 'primary' } = action;

    const variantStyles = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700',
        secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700',
        danger: 'bg-red-600 text-white hover:bg-red-700',
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                variantStyles[variant]
            )}
        >
            {label}
        </button>
    );
}
